<template>
    <div
        data-interface-section="game-container"
        :class="[$styleCrit.container, isFullscreen && $styleCrit.fullscreen]"
    >
        <div :class="$styleCrit.game_column">
            <div
                :class="[
                    $styleCrit.content,
                    (isGameViewHidden || limitedAvailability) && $styleCrit.init_state,
                    appStore.platform === 'android' && !appStore.isNativeAppEnv && $styleCrit.androidContent,
                    appStore.isNativeAppEnv && $styleCrit.nativeContent,
                    !isGameViewHidden && !limitedAvailability && $styleCrit.black_mobile_background,
                ]"
                :style="dominantRgb && { '--dominant-rgb': dominantRgb }"
            >
                <template v-if="props.game && !limitedAvailability">
                    <MobileBarsHider
                        v-model:isTabsOpened="isTabsOpened"
                        :active="appStore.platform === 'ios'"
                        :isGameViewHidden
                    >
                        <GameFrameArea
                            ref="game-frame-area"
                            :class="$styleCrit.game_area_override"
                            :hidden="isGameViewHidden"
                            :game="props.game"
                            :categories="props.categories"
                            :gameFrame="gameFrame"
                            :isFullscreen="isFullscreen"
                            :advancedBannerSlotClass="$styleCrit.optional_slot_override"
                            :isTabsOpened="isTabsOpened"
                            :showMobileInterface="showMobileInterface"
                            :showGameHeader="showMobileInterface"
                            :openDisabledAdModal="() => openDisabledAdModal('ad-blocker-offer')"
                            @closeGame="closeGame"
                            @noAdsClick="openDisabledAdModal('instream-no-ads-button')"
                            @gameFocusChange="isGameFrameFocused = $event"
                        />
                    </MobileBarsHider>
                    <OutOfPageAdContainer
                        ref="out-of-page-ad-container"
                        :adInfo="gameFrame.adInfo.value"
                        :isAdFreeButtonShown="!adDisabledStore.isAdFreeActive && appStore.isPurchaseEnabled"
                        :isAdFreeActive="adDisabledStore.isAdFreeActive"
                        @noAdsClick="openDisabledAdModal('instream-no-ads-button')"
                    />
                </template>
                <template v-if="isGameViewHidden || limitedAvailability">
                    <MobileAppGameInfo
                        v-if="appStore.isNativeAppEnv"
                        :class="[
                            $styleCrit.short_game_info_override,
                            limitedAvailability ? $style.darken_filter_override : '',
                        ]"
                        :game="props.game"
                        :category="category"
                    />
                    <ShortGameInfo
                        v-else
                        :class="[
                            $styleCrit.short_game_info_override,
                            limitedAvailability ? $style.darken_filter_override : '',
                        ]"
                        :game="props.game"
                        :category="category"
                        placement="mobile_game_container"
                    />
                    <figure
                        :class="['sqrcl', $styleCrit.preview_container]"
                        @click="previewClick"
                    >
                        <img
                            :class="[
                                $styleCrit.preview,
                                props.game ? $styleCrit.visible : $styleCrit.hidden,
                                limitedAvailability ? $style.darken_filter_override : '',
                            ]"
                            :src="props.game?.big_preview || props.game?.preview"
                            :srcset="previewSrcset"
                            fetchpriority="high"
                            :alt="tr('seo.preview', { title: props.game?.title ?? '' })"
                            aria-labelledby="game-title"
                        />
                        <FigcaptionText
                            :game="props.game"
                            :categories="props.categories"
                        />
                        <Typography
                            v-if="limitedAvailability"
                            type="paragraph"
                            size="m"
                            accent
                            :class="$styleCrit.limited_availability"
                        >
                            {{ limitedAvailability }}
                        </Typography>
                        <MobileLoginPromo
                            v-else-if="isLoginPromoShown"
                            @shown="topBarLoginPromoAnalytics.shown({ trigger: 'game-page' })"
                            @close="dismissLoginPromo"
                            @login="signIn"
                        />
                    </figure>
                    <PlayButton
                        v-if="limitedAvailability"
                        interfaceTarget="similar_games_button"
                        :label="$tr('gameContainer.similar_games')"
                        :class="$styleCrit.play_button_override"
                        @click="emit('similarGamesPressed')"
                    />
                    <div
                        v-else-if="googlePlayUrl"
                        :class="$styleCrit.play_row"
                    >
                        <PlayButton
                            interfaceTarget="play_button"
                            :label="props.game ? $tr('gameContainer.play') : ''"
                            :class="[$styleCrit.row_button_override, $styleCrit.play_label_override]"
                            @click="handlePlay"
                        />
                        <GooglePlayButton
                            :url="googlePlayUrl"
                            :class="$styleCrit.row_button_override"
                            @click="handleGooglePlayClick"
                        />
                    </div>
                    <PlayButton
                        v-else-if="!isGameStarted"
                        interfaceTarget="play_button"
                        :label="props.game ? $tr('gameContainer.play') : ''"
                        :class="$styleCrit.play_button_override"
                        @click="handlePlay"
                    />
                    <PlayButton
                        v-else
                        interfaceTarget="play_button"
                        :label="$tr('gameContainer.continuePlay')"
                        :class="$styleCrit.play_button_override"
                        @click="toggleFullscreen"
                    />
                    <PlayingFriends
                        v-if="isPlayingFriendsShown"
                        :isTrendingGame
                    />
                    <div
                        v-else
                        :class="$styleCrit.playing_friends_placeholder"
                    />
                    <MobileFooter
                        :class="$styleCrit.mobile_footer_override"
                        :game="props.game"
                        :shareData
                        :isGooglePlayShown="!!googlePlayUrl"
                    />
                </template>
                <NoAdFinalScreen v-if="props.game && !limitedAvailability" />
            </div>
            <DesktopFooter
                ref="desktop-footer"
                :game="props.game"
                :category
                :isFullscreen
                :shareData
                :isGameUnavailable="!isPlatformAvailable"
                :isGameFrameVisible="gameFrame.isGameFrameVisible.value"
                :isGamePaused
                :isHidden="isDesktopFooterUnavailable"
                @toggleFullscreen="toggleFullscreen"
                @togglePlayback="handleToggleGamePlayback"
                @hidePanel="hideDesktopFooter"
            />
        </div>
        <ExportRightColumn
            :key="gameFrame.sidebarRefreshKey.value"
            :class="[$styleCrit.sidebar_override, sidebarShown && $styleCrit.sidebar_enabled_override]"
        />
        <DesktopLoginPromo
            v-if="isLoginPromoShown"
            @shown="topBarLoginPromoAnalytics.shown({ trigger: 'game-page' })"
            @close="dismissLoginPromo"
            @login="signIn"
        />
        <FullscreenLoginPromo
            v-if="sidebarShown"
            :visible="!isFullscreenPromoDismissed"
            :class="$style.fullscreen_promo_override"
            @shown="fullscreenLoginPromoAnalytics.shown({ trigger: 'fullscreen' })"
            @playWithAd="dismissFullscreenPromo"
            @login="handleFullscreenPromoLogin"
        />
    </div>
</template>

<script lang="ts" setup>
import { useElementHover, useMounted } from '@vueuse/core'
import { computed, onMounted, onUnmounted, ref, toRef, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Typography from '@/components/Typography.vue'
import { useBodyScrollLock } from '@/composables/use-body-scroll-lock'
import { usePopupAnalytics } from '@/composables/use-popup-analytics'
import { useTr } from '@/composables/use-tr'
import { useUserInfoLoaded } from '@/composables/use-user-state'
import { NATIVE_APP_CLIDS } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { useNativeExactPackPurchase } from '@/modules/native-payments/native-exact-pack-purchase'
import { SSR_QUERY } from '@/router/query-params'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useLoginNudgeStore } from '@/store/login-nudge-store'
import { useModalPlayOnAppStore } from '@/store/modal-play-on-app-store'
import { useNativeAppPromoRatingStore } from '@/store/native-app-promo-rating-store'
import { useNativeAppStore } from '@/store/native-app-store'
import { usePushSubscriptionStore } from '@/store/push-subscribe/store'
import { usePWAInstallStore } from '@/store/pwa-install/store'
import { useUserStore } from '@/store/user-store/user-store'
import type { APIGameType, GameCategoriesInfoType } from '@/types'
import type { Config } from '@/utils/clid-config'
import { parseDominantRgb } from '@/utils/dominant-color'

import { useGameAutoplayStore } from '../../autoplay-store'
import { useGameAvailability } from '../../composables/use-game-availability'
import { useGameFrame } from '../../composables/use-game-frame'
import { useGameIframeUrl } from '../../composables/use-game-iframe-url'
import { useGamePingSession } from '../../composables/use-game-ping-session'
import { useLoginNudge } from '../../composables/use-login-nudge'
import { useShareData } from '../../composables/use-share-data'
import { useStartGameSound } from '../../composables/use-start-game-sound'
import { buildHbGameInfo, getMainCategory } from '../../utils'
import ExportRightColumn from '../ExportRightColumn.vue'
import GameFrameArea from '../GameFrameArea.vue'
import GooglePlayButton from '../GooglePlayButton.vue'
import MobileAppGameInfo from '../MobileAppGameInfo.vue'
import OutOfPageAdContainer from '../OutOfPageAdContainer/OutOfPageAdContainer.vue'
import PlayButton from '../PlayButton.vue'
import ShortGameInfo from '../ShortGameInfo.vue'
import DesktopFooter from './DesktopFooter/DesktopFooter.vue'
import DesktopLoginPromo from './DesktopLoginPromo.vue'
import FigcaptionText from './FigcaptionText.vue'
import FullscreenLoginPromo from './FullscreenLoginPromo.vue'
import MobileBarsHider from './MobileBarsHider.vue'
import MobileFooter from './MobileFooter/MobileFooter.vue'
import MobileLoginPromo from './MobileLoginPromo.vue'
import NoAdFinalScreen from './NoAdFinalScreen/NoAdFinalScreen.vue'
import PlayingFriends from './PlayingFriends.vue'
import { GOOGLE_PLAY_APPS } from './utils/google-play-app'

const props = defineProps<{
    game: APIGameType | undefined
    categories: GameCategoriesInfoType | undefined
}>()

const emit = defineEmits<{
    similarGamesPressed: []
}>()

const SHORT_SESSION_MIN_PLAY_MS = 30 * 1000 // 30 sec
const SHORT_SESSION_MAX_PLAY_MS = 3 * 60 * 1000 // 3 minutes

const tr = useTr()
const i18nAddon = useI18nAddon()
const router = useRouter()
const route = useRoute()

const userStore = useUserStore()
const appStore = useAppStore()
const gameAutoplayStore = useGameAutoplayStore()
const clientAppStore = useClientAppStore()
const loggerStore = useLoggerStore()
const adDisabledStore = useAdDisabledStore()
const adsStore = useAdsStore()
const layoutStore = useLayoutStore()
const modalPlayOnAppStore = useModalPlayOnAppStore()
const loginNudgeStore = useLoginNudgeStore()
const pwaStore = usePWAInstallStore()
const nativeAppSDK = useNativeAppStore()
const nativeAppPromoRatingStore = useNativeAppPromoRatingStore()
const pushSubscriptionStore = usePushSubscriptionStore()

const isMounted = useMounted()

const { chargeUser: chargeUserWithNativeExactPack } = useNativeExactPackPurchase(userStore.chargeUser)
const userInfoLoaded = useUserInfoLoaded()
const gameFrame = useGameFrame()
const { buildGameIframeUrl } = useGameIframeUrl()
const { loadStartGameSound, playStartGameSound } = useStartGameSound()

const shareData = useShareData(toRef(() => props.game))
const { isPlatformAvailable, limitedAvailability } = useGameAvailability(toRef(() => props.game))

const gameFrameAreaRef = useTemplateRef('game-frame-area')
const outOfPageAdContainerRef = useTemplateRef('out-of-page-ad-container')
const desktopFooterRef = useTemplateRef('desktop-footer')

const isIframeHovered = useElementHover(() => gameFrameAreaRef.value?.$el as HTMLElement | undefined)

function preventIframeFocusLoose(event: PointerEvent) {
    if (isGameViewHidden.value) {
        return
    }
    if (event.target instanceof HTMLIFrameElement) {
        return
    }
    if (event.target instanceof HTMLInputElement) {
        return
    }
    if (event.target instanceof Element && event.target.closest('dialog')) {
        return
    }
    event.preventDefault()
}

const isFullscreen = ref(false)
const isTabsOpened = ref(false)
const isGameStarted = ref(false)
const isGameFrameFocused = ref(true)
const isGameFrameInitialized = ref(false)
const wasShortSessionExited = ref(false) // user left fullscreen within SHORT_SESSION_MAX_PLAY_MS
const isDesktopFooterTooltipShown = ref(false)
const isDesktopFooterHidden = ref(false)
const gameStartedAt = ref<number>()
const shouldShowLoginPromo = ref(true)
const isFullscreenPromoDismissed = ref(false)
const topBarLoginPromoAnalytics = usePopupAnalytics('login-promo-top-bar')
const fullscreenLoginPromoAnalytics = usePopupAnalytics('login-promo-fullscreen')

const { startPingSession, stopPingSession } = useGamePingSession({
    game: toRef(() => props.game),
    isFullscreen,
    lastAdResult: gameFrame.lastAdResult,
})

useLoginNudge(gameFrame.loginPromptTrigger, {
    adStarted: gameFrame.fullscreenAdStarted,
    playedMs: gameFrame.playedMs,
    request: gameFrame.requestLoginPrompt,
})

const isGameViewHidden = computed(
    () => !isGameStarted.value || (appStore.platform !== 'desktop' && !isFullscreen.value),
)
const isGamePaused = computed(() => !isGameFrameFocused.value)

// Expose the original preview in SSR srcset for SEO while displays select the regular preview.
const previewSrcset = computed(() => {
    if (!props.game?.big_preview) {
        return undefined
    }

    return `${props.game.preview} 5x, ${props.game.big_preview} 6x`
})

const dominantRgb = computed(() => parseDominantRgb(props.game?.preview_dominant_color))

const showMobileInterface = computed(() => appStore.platform !== 'desktop' && !!props.game && isGameStarted.value)

const isDesktopFooterUnavailable = computed(
    () => isDesktopFooterHidden.value || (isFullscreen.value && showMobileInterface.value),
)

const isTrendingGame = computed(() => props.game?.categories.includes('trending_now'))

const googlePlayUrl = computed(() => {
    if (limitedAvailability.value || isGameStarted.value) {
        return undefined
    }
    if (appStore.experiments['play-on-google-play']?.sample !== 'gp-button') {
        return undefined
    }
    return props.game?.hru ? GOOGLE_PLAY_APPS[props.game.hru] : undefined
})

watch(
    () => isMounted.value && !!googlePlayUrl.value,
    (isShown) => {
        if (!isShown) {
            return
        }
        loggerStore.logEvent(
            {
                event: 'custom_event',
                eventName: 'google_play_game_shown',
            },
            { flushImmediately: true },
        )
    },
    { immediate: true },
)

const autoplay = computed(() => ({
    organic: gameAutoplayStore.shouldAutoplay(props.game, isPlatformAvailable.value),
    query: !!route.query[SSR_QUERY.AUTOPLAY] && isPlatformAvailable.value && !props.game?.is_hidden,
}))

const isPlayingFriendsShown = computed(
    () => appStore.platform === 'desktop' && isMounted.value && !isGameStarted.value && !limitedAvailability.value,
)

const shouldOrientationBeLocked = computed(
    () =>
        isFullscreen.value &&
        layoutStore.screenOrientation === 'portrait' &&
        !props.game?.vertical &&
        props.game?.horizontal,
)

const adConfig = computed((): Config => {
    // Keep Native App config here so partner clids in clid-helpers don't override portal logic; Native App config should always apply on portal
    if (appStore.isNativeAppEnv && appStore.clid !== NATIVE_APP_CLIDS.twa) {
        return {
            isPrerollDisabled: true,
            isSmartAdDisabled: true,
            inGameAdHandlerConfig: {
                interstitial: { initialDelayInMs: 30_000, timeout: 120_000 },
            },
        }
    }
    // Keep TWA config here so partner clids in clid-helpers don't override portal logic; TWA should always apply on portal
    if (appStore.clid === NATIVE_APP_CLIDS.twa) {
        return {
            isPrerollDisabled: true,
            isPreloaderDisabled: true,
            inGameAdHandlerConfig: {
                interstitial: { initialDelayInMs: 20_000 },
            },
        }
    }

    return {
        isPrerollDisabled: autoplay.value.organic,
        inGameAdHandlerConfig: {
            interstitial: { timeout: 60_000 },
        },
    }
})

const category = computed(() => getMainCategory(props.game, props.categories)?.title)

const sidebarShown = computed(() => {
    const isFullscreenBannerDisabled = !!props.game?.config?.ads?.disableFullscreenBanners

    return (
        gameFrame.isGameFrameVisible.value &&
        isFullscreen.value &&
        !isFullscreenBannerDisabled &&
        !adDisabledStore.isAdFreeActive &&
        !userStore.isAuthorized &&
        !appStore.isNativeAppEnv
    )
})

function dismissFullscreenPromo() {
    isFullscreenPromoDismissed.value = true
    fullscreenLoginPromoAnalytics.closed('play-with-ad')
}

function handleFullscreenPromoLogin() {
    isFullscreenPromoDismissed.value = true
    fullscreenLoginPromoAnalytics.clicked('login')
    layoutStore.openSignInModal('game-fullscreen-no-banner-promo')
}

useBodyScrollLock(
    () => isIframeHovered.value && isGameStarted.value && !isGameViewHidden.value && appStore.platform === 'desktop',
)

function openDisabledAdModal(trigger: string) {
    return adDisabledStore.openDisabledAdModal(trigger, gameFrame.adInfo.value.placement)
}

function closeGame() {
    toggleFullscreen(false)
}

function hideDesktopFooter() {
    isDesktopFooterHidden.value = true
}

function handleToggleGamePlayback() {
    if (isGamePaused.value) {
        gameFrameAreaRef.value?.focusGame()
    } else {
        gameFrameAreaRef.value?.blurGame()
    }
}

function previewClick() {
    if (limitedAvailability.value) {
        return
    }

    if (isGameStarted.value) {
        toggleFullscreen()
    } else {
        handlePlay()
    }
}

async function toggleFullscreen(forceFullscreen?: boolean) {
    isFullscreen.value = forceFullscreen ?? !isFullscreen.value

    // iOS Safari has no Fullscreen API, the .fullscreen class alone does the job there
    if (!document.fullscreenEnabled || appStore.platform === 'ios') {
        return
    }

    try {
        if (isFullscreen.value) {
            await document.documentElement.requestFullscreen()
        } else {
            await document.exitFullscreen()
        }
    } catch {}
}

const isLoginPromoShown = computed(
    () =>
        props.game &&
        shouldShowLoginPromo.value &&
        !isGameStarted.value &&
        userInfoLoaded.value &&
        !userStore.isAuthorized,
)

function dismissLoginPromo() {
    shouldShowLoginPromo.value = false
    topBarLoginPromoAnalytics.closed('dismiss')
}

function signIn() {
    topBarLoginPromoAnalytics.clicked('login')
    layoutStore.openSignInModal('game-container-top-bar')
}

async function openGameArea(): Promise<boolean> {
    if (props.game?.is_hidden && !route.query[SSR_QUERY.SHOW_HIDDEN]) {
        const firstCategory = props.categories?.pathCategories[0]
        if (firstCategory) {
            router.push(firstCategory.href)
        }

        return false
    }

    if (props.game && !isFullscreen.value) {
        await toggleFullscreen(true)
    }

    return true
}

function handlePlay() {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'play',
    })

    onInitScreenClick()
}

function handleGooglePlayClick() {
    loggerStore.logEvent(
        {
            event: 'custom_event',
            eventName: 'google_play_game_clicked',
        },
        { flushImmediately: true },
    )
}

function startGameSession() {
    if (isGameStarted.value) {
        return
    }

    isGameStarted.value = true
    gameStartedAt.value = Date.now()
    nativeAppPromoRatingStore.startGameSession()

    startPingSession()

    if (adDisabledStore.isAdDisabled || adConfig.value.isPrerollDisabled) {
        gameFrame.startGameLoading()
    } else {
        playStartGameSound()
        gameFrame.showPreroll()
    }
}

function tryAutoplay() {
    if (import.meta.env.SSR) {
        return
    }

    const { organic, query } = autoplay.value

    if (organic && isMounted.value && !isGameStarted.value) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game_autoplay',
        })
        startGameSession()
        gameAutoplayStore.markAutoplayStarted()
    }

    if (query && !document.hidden) {
        openGameArea()
    }
}

function syncAutoplay() {
    if (import.meta.env.SSR) {
        return
    }

    tryAutoplay()

    if (autoplay.value.query) {
        document.addEventListener('visibilitychange', tryAutoplay)
    }
}

async function onInitScreenClick() {
    const opened = await openGameArea()
    if (!opened) {
        return
    }

    startGameSession()

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'export-init-click',
        dimension1: 'default',
    })
}

function tryInitGameFrame() {
    if (isGameFrameInitialized.value || !props.game || !isMounted.value) {
        return
    }

    if (!adsStore.adService) {
        console.error('Error: No ad service was passed to the game frame')
        return
    }

    const url = buildGameIframeUrl(props.game)

    if (!url) {
        return
    }

    isGameFrameInitialized.value = true

    adsStore.updateHbGameInfo(buildHbGameInfo(props.game, i18nAddon.getCurrentLanguage()))
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'game_opened',
    })

    const adLoaderSubtitlesByAdType = computed(() => ({
        interstitial_preroll: tr('adLoader.prerollSubtitle'),
    }))

    gameFrame.initGameFrame({
        game: props.game,
        gameUrl: url,
        adConfig: adConfig.value,
        adService: adsStore.adService,
        getAdRootElementId: () => outOfPageAdContainerRef.value?.containerId,
        userApi: {
            // keep the same auth source as the export iframe on the portal for analytics continuity
            authorizeUser: () => userStore.authorizeUser('export-page-portal'),
            getUser: userStore.getUser,
            isGuest: userStore.isGuest,
            chargeUser: chargeUserWithNativeExactPack,
            cancelCharge: userStore.cancelCharge,
        },
        attributes: {
            clid: appStore.clid,
            widgetId: clientAppStore.widgetId,
            userAcquisitionParams: { ...clientAppStore.userAcquisitionParams },
        },
        adBlockerOfferEnabled: true,
        affiliateLoader: false,
        adLoaderSubtitlesByAdType: adLoaderSubtitlesByAdType.value,
    })

    loadStartGameSound()
}

async function onFullscreenChange() {
    isFullscreen.value = document.fullscreenElement !== null
    try {
        if (shouldOrientationBeLocked.value) {
            await window.screen.orientation.lock?.('landscape')
        } else {
            window.screen.orientation.unlock?.()
        }
    } catch {}
}

watch(isFullscreen, (value) => {
    if (!value) {
        if (isGameStarted.value && gameStartedAt.value) {
            wasShortSessionExited.value =
                Date.now() - gameStartedAt.value > SHORT_SESSION_MIN_PLAY_MS &&
                Date.now() - gameStartedAt.value < SHORT_SESSION_MAX_PLAY_MS
        }
        nativeAppPromoRatingStore.showPromoRating()
        isDesktopFooterHidden.value = false
    }

    layoutStore.setFullscreen(value)
    modalPlayOnAppStore.setBlockedByFullScreen(value)

    nativeAppSDK.postMessage('changeOrientation', {
        action: shouldOrientationBeLocked.value ? 'lockLandscape' : 'unlock',
    })

    const TOOLTIP_DELAY_MS = 15 * 60 * 1000 // 15 minutes

    if (
        !value &&
        !isDesktopFooterTooltipShown.value &&
        gameStartedAt.value &&
        Date.now() - gameStartedAt.value >= TOOLTIP_DELAY_MS &&
        !userStore.isAuthorized &&
        loginNudgeStore.desktopFooterTooltipTimesShown < 3 &&
        appStore.platform === 'desktop'
    ) {
        desktopFooterRef.value?.showLoginTooltip()
        loginNudgeStore.markFooterDesktopTooltipShown()
        isDesktopFooterTooltipShown.value = true
    }
})

watch(
    () => [pwaStore.hasDeferredPrompt, isFullscreen.value],
    ([, isFs]) => {
        if (appStore.platform === 'desktop' && !isFs) {
            if (appStore.isPWA) {
                pushSubscriptionStore.showSubscriptionPrompt({ trigger: 'fullscreen-exit' })
            } else if (!pwaStore.showingNativePWA && isGameStarted.value && !pwaStore.pwaHasShowed) {
                if (pwaStore.hasDeferredPrompt) {
                    pwaStore.showPWAGameInstallPrompt(props.game?.icon)
                } else {
                    pwaStore.showPWAInstallPrompt({
                        trigger: 'game-page',
                        force: true,
                        showTimeoutMs: 40_000,
                        isFullScreenAllowed: false,
                    })
                }
            }
        }
    },
)

watch(
    () => adDisabledStore.adDisabledModalOptions.isOpened || layoutStore.isPaymentModalOpen,
    (isModalOpen) => {
        adsStore.adService?.setOutOfPageAdPlaybackState(!isModalOpen)
    },
)

watch(
    () => props.game,
    () => {
        tryInitGameFrame()
        syncAutoplay()
    },
)

onMounted(() => {
    if (document.fullscreenEnabled) {
        document.addEventListener('fullscreenchange', onFullscreenChange)
    }
    document.addEventListener('pointerdown', preventIframeFocusLoose)

    tryInitGameFrame()
    syncAutoplay()
})

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    document.removeEventListener('visibilitychange', tryAutoplay)
    document.removeEventListener('pointerdown', preventIframeFocusLoose)
    layoutStore.setFullscreen(false)
    nativeAppSDK.postMessage('changeOrientation', { action: 'unlock' })
    if (appStore.platform === 'desktop') {
        pwaStore.forceHidePWAInstallPrompt()
    }
    gameFrame.destroy()
    stopPingSession()
    adsStore.updateHbGameInfo(null)
})
</script>
<style module="$styleCrit" critical>
.visible {
    opacity: 1;
}

.hidden {
    opacity: 0;
}

.container {
    --container-border-radius: 12px;
    --media-width: clamp(230px, 80%, 800%);

    background: #1d1c26;
    border-radius: var(--container-border-radius);
    display: flex;
    flex-direction: column;
    position: relative;
}

.game_column {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
    min-height: 0;
}

.content {
    --cover-rgb: var(--dominant-rgb, 99, 50, 161);

    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    flex-grow: 1;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    border-radius: var(--container-border-radius) var(--container-border-radius) 0 0;
    background: var(--experimental-purple-graphit)
        linear-gradient(180deg, rgb(var(--cover-rgb)) 10%, var(--experimental-purple-graphit) 100%) no-repeat;
}

.preview_container {
    margin: 0;
    position: relative;
    contain: strict;
    cursor: pointer;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    width: var(--media-width);
    grid-template: 'main' 100% / 100%;
    display: grid;
    border-radius: var(--radius-12);
    --sqrcl: var(--radius-12) * 2;
}

.preview {
    grid-area: main;
    object-fit: cover;
    width: 100%;
    height: 100%;
}

.limited_availability {
    position: absolute;
    inset: auto 4px 4px;
    border-radius: var(--radius-24);
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    padding-block: 8px;
    text-align: center;
    visibility: hidden;
    min-height: 24px;
}

.play_button_override {
    width: var(--media-width);
    visibility: hidden;
}

.play_row {
    width: var(--media-width);
    visibility: hidden;
    display: flex;
    gap: 8px;
}

.row_button_override {
    flex: 1 1 0;
    min-width: 0;
}

.play_row .play_label_override * {
    font-size: 22px;
}

@supports (font-size: 1cqw) {
    .play_row .play_label_override * {
        font-size: clamp(12px, 22cqi, 22px);
    }
}

/* the game area mimics the export iframe: contain: strict isolates layout and clips
   fixed/absolute overlays the same way the iframe document did */
.game_area_override {
    width: 100%;
    height: 100%;
    background: #000;
    contain: strict;
}

.sidebar_override {
    display: none;
}

.short_game_info_override {
    align-self: flex-start;
}

.short_game_info_override,
.mobile_footer_override {
    visibility: hidden;
    height: 48px;
}

.playing_friends_placeholder {
    display: none;
}

@media (--mobile) {
    .container {
        --container-border-radius: 0;
        margin: 0 calc(var(--global-padding-horizontal) * -1) 0;
    }

    .container.fullscreen {
        margin: 0;
    }

    .container.fullscreen .content {
        height: auto;
    }

    .content.init_state {
        padding: 6px;
    }

    .content {
        --cover-rgb: var(--dominant-rgb, 151, 71, 255);

        height: 100%;
        gap: 12px;

        /* the stops are translucent, so they must composite over carbon and not over .container */
        background-color: var(--carbon-100);
        background-image: linear-gradient(
            0deg,
            rgba(var(--cover-rgb), 0) 0%,
            rgba(var(--cover-rgb), 0.41) 10%,
            rgba(var(--cover-rgb), 0.68) 30%,
            rgba(var(--cover-rgb), 1) 50%,
            rgba(var(--cover-rgb), 0.68) 70%,
            rgba(var(--cover-rgb), 0.41) 90%,
            rgba(var(--cover-rgb), 0) 100%
        );
    }

    .preview_container {
        width: calc(100% - 8rem);
        border-radius: calc(var(--radius-12) * 2);
        --sqrcl: var(--radius-12) * 4;
    }

    .play_button_override,
    .play_row {
        width: 100%;
    }

    .black_mobile_background {
        background: var(--carbon-100);
    }
}

@media (--mobile-portrait) {
    .fullscreen .androidContent {
        padding-top: var(--pg-safe-area-top);
    }

    .fullscreen .nativeContent {
        padding-bottom: var(--pg-safe-area-bottom);
    }
}

@media (--mobile-landscape) {
    .content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr auto 1fr;
        grid-template-areas:
            'preview short-info'
            'preview play-button'
            'preview footer';
        background: var(--carbon-100);
    }

    .game_area_override {
        grid-column: span 2;
        grid-row: span 3;
    }

    .preview_container {
        grid-area: preview;
    }

    .short_game_info_override {
        grid-area: short-info;
        align-self: end;
    }

    .play_button_override,
    .play_row {
        grid-area: play-button;
    }

    .mobile_footer_override {
        grid-area: footer;
        align-self: start;
    }
}

@media (--tablet-plus) {
    .content {
        --media-width: clamp(230px, 50%, 50%);
    }

    .container {
        aspect-ratio: var(--thumbnail-aspect-ratio);
        height: auto;
    }

    .container.fullscreen {
        aspect-ratio: unset;
        height: 100dvh;
    }

    .short_game_info_override {
        display: none;
    }
}

@media (--desktop-small-plus) {
    .container {
        height: 100%;
        width: 100%;
        min-height: auto;
        aspect-ratio: auto;
    }

    .init_state {
        padding-top: 52px;
    }
}

@media (--desktop-medium-plus) {
    .content {
        --media-width: clamp(230px, 40%, 40%);
    }

    .playing_friends_placeholder {
        display: block;
        height: 40px;
    }
}

@media (--right-column-fits) {
    .container.fullscreen:has(.sidebar_enabled_override) {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
    }

    .fullscreen .sidebar_enabled_override {
        display: block;
        overflow: hidden;
    }

    .fullscreen:has(.sidebar_enabled_override) .optional_slot_override {
        display: none;
    }
}

.fullscreen {
    --container-border-radius: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 100dvh;
    z-index: 1;
    /* INP optimization: apply touch-action to container instead of all descendants  */
    touch-action: manipulation;
}

.fullscreen .content {
    flex-grow: 1;
    padding-left: var(--pg-safe-area-left);
    padding-right: var(--pg-safe-area-right);
    padding-top: var(--pg-safe-area-top);
}

.fullscreen .androidContent {
    padding: 0;
}

@media (--mobile-landscape) {
    .fullscreen .content {
        padding-right: 0;
    }

    .fullscreen .nativeContent {
        padding-left: 0;
    }
}
</style>
<style module>
.darken_filter_override {
    filter: brightness(65%);
}

@media (--tablet-plus) {
    .limited_availability {
        border-radius: var(--radius-12);
    }
}

.limited_availability,
.play_button_override,
.play_row,
.short_game_info_override,
.mobile_footer_override {
    visibility: visible;
}

.fullscreen_promo_override {
    display: none;
    position: absolute;
    top: 16px;
    right: calc(var(--global-right-pannel-width) + 16px);
    z-index: 2;
}

@media (--right-column-fits) {
    .fullscreen_promo_override {
        display: flex;
    }
}
</style>
